// ---- NPC dressing ----
// The per-instance layer that runs AFTER buildNpc: socket-slot items (a held
// weapon seated into handMain exactly like the player's — same equipRig, same
// per-weapon hold — plus an offhand attachment) and the seeded gait jitter.
// Anchor slots (helmets/packs) are geometry and already emitted by
// buildFromDoc; THIS module owns everything that needs the item registry or
// the live THREE group, which buildNpc/define.ts must not import (cycle:
// items/index → load.ts → creature.ts → define.ts).
//
// Deterministic across every client + the studio: the only input is the spawn
// id (variantSeed), which already rides the wire.
//
// THREE-but-DOM-free. Call sites: the client NPC manager (npc/index.ts) and
// the studio catalog — anywhere an NPC group is built for display.

import type * as THREE from "three";
import type { NpcSpec } from "./define.ts";
import type { ModelDoc } from "../model/parts.ts";
import { resolvedModelDoc } from "../model/index.ts";
import {
  variantSeed, seededSigned, seededUnit, LANE_GAIT_PHASE, LANE_GAIT_SPEED,
} from "../model/variants.ts";
import { collectRig } from "../model/rig.ts";
import { equipRig, equipTool, seatWeapon, effectiveHold } from "../anim/pose.ts";
import { buildItem, isHeldTool } from "../items/define.ts";
import { itemSpecOrNull } from "../items/index.ts";

/** Dress a built model group from its RESOLVED doc + variation seed: seat
 *  socket-slot items + set gait jitter. The low-level half — dressNpc wraps it
 *  for specs; the studio workbench calls it directly with synthetic docs/seeds. */
export function dressModel(group: THREE.Group, doc: ModelDoc, seed: number): void {
  // Socket slots — held weapon into handMain (full equip: combat stance flags
  // and future NPC swings read the same userData the player uses), held TOOL
  // (fishing rod / hatchet) via equipTool (same main-hand seat as the player),
  // offhand as a plain seated attachment.
  const held = doc.slots?.held;
  const off = doc.slots?.offhand;
  if (held || off) {
    const rig = collectRig(group);
    if (held) {
      const item = itemSpecOrNull(held);
      if (item?.weapon) equipRig(group, rig, item);
      else if (item && isHeldTool(item)) equipTool(group, rig, item);
    }
    if (off && rig.handOff) {
      const item = itemSpecOrNull(off);
      if (item?.weapon) {
        const mesh = buildItem(item);
        seatWeapon(mesh, effectiveHold(item.weapon.archetype, item.weapon.hold));
        rig.handOff.add(mesh);
      }
    }
  }

  // Seeded gait desync (consumed by animateGait).
  const varn = doc.variation;
  if (varn?.gaitPhase || varn?.gaitSpeed) {
    group.userData.gaitJitter = {
      phase: varn.gaitPhase ? seededUnit(seed, LANE_GAIT_PHASE) * Math.PI * 2 : 0,
      speed: varn.gaitSpeed ? seededSigned(seed, LANE_GAIT_SPEED) * varn.gaitSpeed : 0,
    };
  }
}

/** Dress a freshly built NPC group: resolve its model doc + spawn seed, then
 *  dressModel. No-op for NPCs whose model has no slots/variation. */
export function dressNpc(group: THREE.Group, spec: NpcSpec): void {
  const doc = spec.model ? resolvedModelDoc(spec.model) : undefined;
  if (doc) dressModel(group, doc, variantSeed(spec.id));
}
